import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { PasswordNotValidError } from '@adornis/buchungsheld-db/errors/PasswordNotValidError.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './actions/bh-page-action-login.js';

@customElement('bh-page-reset-password')
export class BHPageAction extends BuchungsheldLitElement {
  @state() private readonly token = new RXController(this, routingState.searchParams.get('token'));

  @state() private password = '';
  @state() private passwordRepeat = '';
  @state() private _error = '';
  @state() private _success = false;

  override styles() {
    return [
      ...super.styles(),
      { ':host': { display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' } },
    ];
  }

  override render() {
    if (this._success)
      return html`<x-card>
        <x-flex space="md">
          <tr-h4 bold>${this._translation.translate('actions', 'that_worked')}.</tr-h4>
          <tr-text>${this._translation.translate('actions', 'password_change_success')}</tr-text>
          <tr-button href="/profile">${this._translation.translate('actions', 'to_login')}</tr-button>
        </x-flex>
      </x-card> `;

    return html` <x-card>
      <x-flex space="md">
        <x-icon
          ${css({
            alignSelf: 'center',
            background: this.colors.accent,
            padding: this.spacing.sm,
            fontSize: '1.5em',
            borderRadius: '50%',
            color: '#fff',
          })}
        >
          restart_alt
        </x-icon>
        <tr-input
          type="password"
          placeholder="Neues Passwort"
          @value-changed=${(e: ValueEvent<string>) => (this.password = e.detail.value)}
        ></tr-input>
        <tr-input
          type="password"
          placeholder="Passwort wiederholen"
          @value-changed=${(e: ValueEvent<string>) => (this.passwordRepeat = e.detail.value)}
        ></tr-input>
        ${this._error ? html`<tr-text small tone="error"> ${this._error} </tr-text>` : nothing}
        <x-button @click=${() => this.setNewPassword()}> ${this._translation.translate('save')} </x-button>
      </x-flex>
    </x-card>`;
  }

  private async setNewPassword() {
    this._error = '';

    if (!this.password) {
      this._error = this._translation.translate('actions', 'inp_password') as string;
      return;
    }
    if (this.password !== this.passwordRepeat) {
      this._error = this._translation.translate('actions', 'pwd_err_identical') as string;
      return;
    }

    if (!this.token.value) {
      this._error = this._translation.translate('err', 'unexpected') as string;
      return;
    }

    try {
      await User.changePasswordWithToken(this.token.value, this.password, true)();
      await XSnackbar.show(this._translation.translate('actions', 'password_change_success'));
      this._success = true;
    } catch (err: any) {
      console.log(err._class, err);
      switch (err._class) {
        case ValidationError._class:
        case PasswordNotValidError._class:
          this._error = this._translation.translate('actions', 'pwd_err_length') as string;
          break;
        default:
          this._error = err.message;
      }
    }
  }
}
